import { useCallback, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ApiClient, useApiClient } from './apiClient';

export interface UseApiMutationResult {
  /** True while any mutation via `run` is in flight. */
  loading: boolean;
  error: string | null;
  /** Resets `error` to `null` — use when dismissing an inline error banner. */
  clearError: () => void;
  /**
   * Execute an action with shared `loading` / `error` state.
   * Re-throws on failure so callers can branch on success / failure.
   *
   * @param action Function that receives the memoised `ApiClient` and performs the request.
   * @param errorMessage Fallback message when `action` rejects with a non-`Error` value.
   */
  run: <T>(
    action: (apiClient: ApiClient) => Promise<T>,
    errorMessage?: string,
  ) => Promise<T>;
}

export interface UseApiMutationOptions {
  apiClientOverride?: ApiClient;
}

/**
 * Thin wrapper for mutation hooks. Manages shared `loading` / `error` state so
 * callers can define any number of domain-specific mutations without repeating
 * the try / catch / setError boilerplate.
 *
 * @example
 * const { loading, error, run } = useApiMutation();
 * const approve = useCallback(
 *   (periodId: string) =>
 *     run(
 *       (c) => c.post<TimesheetPeriod>(`/timesheets/${periodId}/approve`),
 *       'Failed to approve timesheet',
 *     ),
 *   [run],
 * );
 */
export function useApiMutation(
  options: UseApiMutationOptions = {},
): UseApiMutationResult {
  const { apiClientOverride } = options;
  const { getAccessToken } = useAuth();
  const defaultApiClient = useApiClient(getAccessToken);
  const apiClient = apiClientOverride ?? defaultApiClient;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const run = useCallback(
    async <T,>(
      action: (apiClient: ApiClient) => Promise<T>,
      errorMessage?: string,
    ): Promise<T> => {
      setLoading(true);
      setError(null);
      try {
        return await action(apiClient);
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : errorMessage ?? 'Request failed';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [apiClient],
  );

  return { loading, error, clearError, run };
}
